import store from '@/store'
import { requestOpenAI } from './openaiApi'
import { requestGemini } from './geminiApi'
import { requestClaude } from './claudeApi'
import { aiResponseError } from './httpClient'
import { classifyAiError } from './aiErrors'
import { trackAiGeneration } from '@/utils/helpHero'

const MAX_ATTEMPTS = 3
// Beyond this a retry stalls the turn longer than it is worth; we surface the wait instead.
const MAX_RETRY_WAIT_MS = 30000

const dispatchRequest = (aiVersion, params) => {
  if (aiVersion < 10) {
    return requestOpenAI(params)
  }

  if (aiVersion < 15) {
    return requestGemini(params)
  }

  return requestClaude(params)
}

export const sendRequest = (aiVersion, params, useOwnAiKey = store.state.workflow.useOwnAiKey) => {
  return dispatchRequest(aiVersion, { ...params, useOwnAiKey })
}

const delay = (ms, signal) => new Promise((resolve, reject) => {
  // An abort listener added after the signal already fired never runs, so check first.
  if (signal?.aborted) {
    reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))

    return
  }

  const onAbort = () => {
    clearTimeout(timer)
    reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
  }

  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort)
    resolve()
  }, ms)

  signal?.addEventListener('abort', onAbort, { once: true })
})

// A rate limit must not end an agentic turn: dying here loses every round already applied.
const sendRequestWithRetry = async (aiVersion, params) => {
  let attempt = 1
  let spentMs = 0

  while (true) {
    try {
      return await sendRequest(aiVersion, params)
    } catch (error) {
      if (params.signal?.aborted || error?.name === 'AbortError') {
        throw error
      }

      const { retryable, retryAfterMs } = classifyAiError(error)
      const backoffMs = Math.min(1000 * (2 ** (attempt - 1)), 8000)
      // A provider-supplied "0s" must not mean "retry instantly against the same limit".
      const waitMs = Math.max(retryAfterMs || 0, backoffMs)

      if (!retryable || attempt >= MAX_ATTEMPTS || spentMs + waitMs > MAX_RETRY_WAIT_MS) {
        throw error
      }

      console.warn(`AI request failed (attempt ${attempt}/${MAX_ATTEMPTS}), retrying in ${waitMs}ms:`, error?.message)

      if (typeof params.onRetry === 'function') {
        params.onRetry({ attempt, waitMs })
      }

      await delay(waitMs, params.signal)
      spentMs += waitMs
      attempt++
    }
  }
}

const checkParseError = (obj) => {
  if (obj?.message && obj.message.includes('Failed to parse AI response')) {
    throw new Error(obj.message)
  }
}

export const fetchJsonResponse = async ({ messages, attachments, aiVersion, tools, signal, promptCacheKey, onRetry }) => {
  const params = {
    messages,
    attachments,
    aiVersion,
    tools,
    streamText: false,
    returnReader: false,
    signal,
    promptCacheKey,
    onRetry
  }

  trackAiGeneration()

  return sendRequestWithRetry(aiVersion, params)
}

export const streamJsonResponses = async ({ messages, attachments, aiVersion, onChunkReceived, signal }) => {
  if (typeof onChunkReceived !== 'function') {
    throw new Error('streamJsonResponses: onChunkReceived callback is required.')
  }

  const allReceivedObjects = []
  const params = {
    messages,
    attachments,
    aiVersion,
    streamText: false,
    returnReader: true,
    signal
  }

  const delimiter = 'jsonobjectdelimiter'

  const readStream = async (reader) => {
    if (!reader) {
      return
    }

    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        if (signal?.aborted) {
          await reader.cancel()
          break
        }

        const { value, done } = await reader.read()

        if (done) {
          const trimmedBuffer = buffer.trim()

          if (trimmedBuffer) {
            try {
              const finalObj = JSON.parse(trimmedBuffer)
              checkParseError(finalObj)
              await onChunkReceived(finalObj)
              allReceivedObjects.push(finalObj)
            } catch (e) {
              if (e.message && e.message.includes('Failed to parse AI response')) {
                throw e
              }
              console.error('Error parsing final buffered JSON chunk:', e, 'Buffer:', trimmedBuffer)
            }
          }
          break
        }

        const textChunk = decoder.decode(value, { stream: true })

        let response
        try {
          response = JSON.parse(textChunk)
        } catch (e) {
        }

        if (response?.error?.message) {
          throw aiResponseError(response.error)
        }

        buffer += textChunk

        let delimiterIndex
        while ((delimiterIndex = buffer.indexOf(delimiter)) !== -1) {
          const jsonString = buffer.substring(0, delimiterIndex)
          buffer = buffer.substring(delimiterIndex + delimiter.length)

          const trimmedJsonString = jsonString.trim()

          if (trimmedJsonString) {
            try {
              const obj = JSON.parse(trimmedJsonString)
              checkParseError(obj)
              await onChunkReceived(obj)
              allReceivedObjects.push(obj)
            } catch (e) {
              if (e.message && e.message.includes('Failed to parse AI response')) {
                throw e
              }
              console.error('Error parsing streamed JSON object:', e, 'Failed JSON string:', trimmedJsonString)
            }
          }
        }
      }
    } catch (error) {
      if (signal && !signal.aborted) {
        try {
          await reader.cancel()
        } catch (e) {
        }
      }
      throw error
    }
  }

  trackAiGeneration()
  await readStream(await sendRequest(aiVersion, params))

  return allReceivedObjects
}

export const streamTextResponse = async ({ messages, aiVersion, tools, onChunkReceived, signal, onRetry }) => {
  if (typeof onChunkReceived !== 'function') {
    throw new Error('streamTextResponse: onChunkReceived callback is required.')
  }

  const params = {
    messages,
    aiVersion,
    tools,
    streamText: true,
    returnReader: true,
    signal,
    onRetry
  }

  const readStream = async (reader) => {
    if (!reader) {
      return
    }

    const decoder = new TextDecoder()

    try {
      while (true) {
        if (signal?.aborted) {
          await reader.cancel()
          break
        }

        const { value, done } = await reader.read()

        if (done) {
          break
        }

        const textChunk = decoder.decode(value, { stream: true })

        let response
        try {
          response = JSON.parse(textChunk)
        } catch (e) {
        }

        if (response?.error?.message) {
          throw aiResponseError(response.error)
        }

        await onChunkReceived(textChunk)
      }
    } catch (error) {
      if (signal && !signal.aborted) {
        try {
          await reader.cancel()
        } catch (e) {
        }
      }
      throw error
    }
  }

  trackAiGeneration()
  await readStream(await sendRequest(aiVersion, params))
}
